/**
 * ============================================================================
 * RFLXNS Theme — Global App Shell Runtime
 * ============================================================================
 * Owns storefront behaviors that should be available across routes:
 * - header scroll state
 * - search overlay open / close
 * - account transition overlay
 * - mobile menu state
 * - in-page anchor scrolling
 * - desktop cursor glow
 *
 * This file intentionally does not own homepage-only, product-only, or
 * collection-only interactions. Those live in route-scoped modules.
 * ============================================================================
 */
document.addEventListener('DOMContentLoaded', () => {
  // --------------------------------------------------------------------------
  // Shared DOM references
  // --------------------------------------------------------------------------
  const nav = document.getElementById('main-nav');
  const searchToggle = document.getElementById('search-toggle');
  const searchOverlay = document.getElementById('search-overlay');
  const searchClose = document.getElementById('search-close');
  const searchInput = document.getElementById('search-input');
  const homeMobileDockSearch = document.getElementById('home-mobile-dock-search');
  const accountTransitionLinks = document.querySelectorAll('[data-account-transition]');
  const accountTransitionOverlay = document.getElementById('account-transition-overlay');
  const mobileMenuTrigger = document.getElementById('mobile-menu-trigger');
  const mobileMenu = document.getElementById('mobile-menu');
  const mobileMenuClose = document.getElementById('mobile-menu-close');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (nav) {
    const handleNavScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 20;

      // Toggle glassmorphism (scrolled class applies the backdrop blur and background)
      nav.classList.toggle('scrolled', isScrolled);
      document.body.classList.toggle('scrolled', isScrolled);
      nav.classList.remove('nav-hidden');
    };

    window.addEventListener('scroll', handleNavScroll, { passive: true });
    handleNavScroll();
  }

  let lastActiveSearchTrigger = null;

  if (searchOverlay && searchClose) {

    const searchCfg = (() => {
      try {
        return JSON.parse(document.getElementById('search-config')?.textContent || '{}');
      } catch { return {}; }
    })();

    // ── Pure helpers (testable; exposed on window.RFLXNSSearchInternals) ─────
    // These are side-effect-free transforms shared by the overlay renderers and
    // a dev-only test harness. They add NO behavior to the overlay on their own;
    // later tasks wire them into rendering.

    /**
     * Left-zero-pad a numeric segment to a minimum width of three digits.
     * Returns null when the input is not a clean run of digits so callers can
     * fall back to the raw title. Never truncates values with 4+ digits.
     * (Requirement 9.2)
     */
    const padNumberSegment = (raw) => {
      const digits = String(raw).trim();
      if (!/^\d+$/.test(digits)) return null;
      return digits.padStart(3, '0');
    };

    /**
     * Render a Product_Card name in the Naming_Convention format
     * `[NAME] / [NUMBER] / [DESCRIPTOR]`, all uppercase. Accepts either a
     * structured `{ name, number, descriptor }` object or a `{ title }` object.
     * A segment counts as supplied only when it is a string with >=1
     * non-whitespace char. When all three are supplied the number is
     * zero-padded via padNumberSegment; otherwise it falls back to the
     * uppercased raw title and never emits a partial ` / ` separator.
     * (Requirements 9.1, 9.2, 9.3, 9.4, 12.4)
     */
    const formatProductName = (input) => {
      const src = input || {};
      const seg = (s) => (typeof s === 'string' && s.trim().length > 0) ? s.trim() : null;
      const name = seg(src.name);
      const descriptor = seg(src.descriptor);
      const number = src.number != null ? padNumberSegment(src.number) : null;
      if (name && number && descriptor) {
        return `${name} / ${number} / ${descriptor}`.toUpperCase();
      }
      return String(src.title || '').toUpperCase();
    };

    /**
     * Format a price amount as a whole-number INR string ("₹N,NNN") with the ₹
     * symbol immediately before the amount and no paise/decimal fraction.
     * (Requirements 10.1, 10.2)
     * Handles decimal strings from suggest.json (e.g. "1499.00" or "1299.00"),
     * whole rupee amounts, or raw paise integers (e.g. 149900).
     */
    const formatINR = (rawPrice) => {
      if (rawPrice == null || rawPrice === '') return '';
      let rupees;
      const num = Number(rawPrice);
      if (isNaN(num)) return '';

      // Shopify suggest.json returns prices as decimal strings, e.g. "1499.00" or "1299.00"
      if (typeof rawPrice === 'string' && rawPrice.includes('.')) {
        rupees = Math.round(parseFloat(rawPrice));
      } else if (num >= 10000 && Number.isInteger(num)) {
        // Raw paise fallback (e.g. 149900 paise -> 1499)
        rupees = Math.round(num / 100);
      } else {
        rupees = Math.round(num);
      }

      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(rupees);
    };

    /**
     * Identify a Test_SKU. Prefers an explicit test tag (case-insensitive);
     * only when product tags are unavailable does it fall back to the
     * last-resort ₹500 (50000 paise) price rule. (Requirements 8.2, 8.3, 8.4)
     */
    const isTestSku = (product, cfg) => {
      const tag = (cfg && cfg.testTag) || 'test';
      if (Array.isArray(product.tags)) {
        return product.tags.some(t => String(t).toLowerCase() === tag.toLowerCase());
      }
      return Number(product.price) === 50000 || Number(product.price) === 500;
    };

    /**
     * Remove every Test_SKU from a product list, preserving source order.
     * (Requirements 8.1, 5.4, 6.4)
     */
    const excludeTestSkus = (products, cfg) => products.filter(p => !isTestSku(p, cfg));

    /**
     * Decide whether to show a Scarcity_Badge and the clamped count to display.
     * Null/NaN inventory hides the badge; inventory is truncated to a whole
     * number and clamped to >= 0; the badge shows only at/below the threshold.
     * (Requirements 7.3, 7.4, 7.5, 7.6, 12.5)
     */
    const scarcity = (inventory, threshold) => {
      if (inventory == null || Number.isNaN(Number(inventory))) return { show: false, count: 0 };
      const inv = Math.trunc(Number(inventory));
      const count = Math.max(0, inv);
      return { show: inv <= threshold, count };
    };

    /**
     * Select the first min(count, available) products in source order, with no
     * padding or substitution. (Requirements 5.1, 5.7)
     */
    const selectFeatured = (products, count) => {
      const list = products || [];
      return list.slice(0, Math.min(count, list.length));
    };

    // Expose helpers read-only so a dev-only test harness can import them
    // without a theme build. Data + functions only; no overlay behavior.
    window.RFLXNSSearchInternals = Object.freeze({
      padNumberSegment,
      formatProductName,
      formatINR,
      isTestSku,
      excludeTestSkus,
      scarcity,
      selectFeatured,
    });

    // ── localStorage helpers ───────────────────────────────────────────────
    const LS_SEARCHES = 'rflxns_searches';
    const LS_CLICKS   = 'rflxns_clicks';
    const getLS  = (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } };
    const setLS  = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
    const saveSearch = (q) => {
      if (!q || q.length < 2) return;
      let a = getLS(LS_SEARCHES).filter(s => s !== q); a.unshift(q);
      setLS(LS_SEARCHES, a.slice(0, 5));
    };
    const saveClick = (data) => {
      let a = getLS(LS_CLICKS).filter(p => p.url !== data.url); a.unshift(data);
      setLS(LS_CLICKS, a.slice(0, 4));
    };

    // ── Keyboard navigation ────────────────────────────────────────────────
    let kbIndex = -1;
    let savedScrollY = 0;
    const getCards = () => {
      const state = searchOverlay?.dataset?.searchState;
      if (state === 'pre-typing' || !state) {
        return [...document.querySelectorAll('#default-search-suggestions .search-result-card')];
      }
      return [...document.querySelectorAll('#api-search-results .search-result-card')];
    };
    const setKbFocus = (cards, idx) => {
      kbIndex = idx;
      cards.forEach((c, i) => {
        const active = i === idx;
        c.classList.toggle('is-kb-focused', active);
        c.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) c.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    };
    document.addEventListener('keydown', (e) => {
      if (!searchOverlay.classList.contains('active')) return;
      const cards = getCards();
      if (e.key === 'Escape')     { closeSearchOverlay(); return; }
      if (e.key === 'ArrowDown')  { e.preventDefault(); setKbFocus(cards, Math.min(kbIndex + 1, cards.length - 1)); return; }
      if (e.key === 'ArrowUp')    { e.preventDefault(); const ni = kbIndex - 1; if (ni < 0) { kbIndex = -1; cards.forEach(c => { c.classList.remove('is-kb-focused'); c.setAttribute('aria-selected', 'false'); }); searchInput?.focus(); } else setKbFocus(cards, ni); return; }
      if (e.key === 'Enter' && kbIndex >= 0 && cards[kbIndex]) { e.preventDefault(); cards[kbIndex].click(); return; }
    });

    // ── Stagger + click tracking ───────────────────────────────────────────
    const staggerCards = (nodes) => {
      nodes.forEach((c, i) => {
        c.style.setProperty('--i', i);
        c.classList.remove('card-in');
        void c.offsetWidth;
        c.classList.add('card-in');
      });
    };
    const bindTracking = (nodes) => {
      nodes.forEach(c => {
        c.addEventListener('click', () => {
          saveClick({
            title: c.querySelector('.search-result-card__name')?.textContent || '',
            url:   c.getAttribute('href') || '',
            price: c.querySelector('.search-result-card__price')?.textContent || '',
            image: c.querySelector('img')?.src || '',
          });
          if (searchInput?.value) saveSearch(searchInput.value.trim());
        });
      });
    };

    // ── Typing placeholder animation ───────────────────────────────────────
    const phrases = ['boxy fit tee...', 'oversized hoodie...', 'waffle texture...', 'drop shoulder...', 'relaxed silhouette...'];
    let pIdx = 0, cIdx = 0, deleting = false, typingT = null;
    const stopTyping = () => {
      clearTimeout(typingT); typingT = null;
      if (searchInput) searchInput.placeholder = 'What are you looking for?';
    };
    const startTyping = () => {
      if (!searchInput || searchInput.value.length > 0) return;
      stopTyping();
      const tick = () => {
        if (searchInput.value.length > 0) { stopTyping(); return; }
        const phrase = phrases[pIdx];
        deleting ? cIdx-- : cIdx++;
        searchInput.placeholder = phrase.slice(0, cIdx);
        if (!deleting && cIdx === phrase.length) { deleting = true; typingT = setTimeout(tick, 1800); return; }
        if (deleting && cIdx === 0) { deleting = false; pIdx = (pIdx + 1) % phrases.length; }
        typingT = setTimeout(tick, deleting ? 42 : 78);
      };
      typingT = setTimeout(tick, 500);
    };

    // ── Recent bar renderer ────────────────────────────────────────────────
    const recentBar = document.getElementById('recent-searches-bar');
    const renderRecentBar = () => {
      if (!recentBar) return;
      const searches = getLS(LS_SEARCHES);
      const clicks   = getLS(LS_CLICKS);
      if (!searches.length && !clicks.length) { recentBar.style.display = 'none'; return; }
      let html = '';
      if (searches.length) {
        html += '<div class="search-recent-row"><span class="search-recent-label">Recent</span><div class="search-recent-chips">';
        searches.forEach(s => {
          html += `<button class="search-recent-chip" data-q="${s}"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>${s}</button>`;
        });
        html += '</div></div>';
      }
      if (clicks.length) {
        html += '<div class="search-recent-section"><span class="search-results-title">Recently Viewed</span><div class="search-results-grid">';
        clicks.forEach((p, i) => {
          const img = p.image ? `<img class="search-result-card__img" src="${p.image}" alt="${p.title}" loading="lazy">` : `<div class="search-result-card__img"></div>`;
          const safePrice = (p.price && !/^₹\s*\d{1,2}$/.test(p.price.trim())) ? p.price : '';
          html += `<a href="${p.url}" class="search-result-card" style="--i:${i}">${img}<div class="search-result-card__body"><span class="search-result-card__name">${p.title}</span><span class="search-result-card__price">${safePrice}</span></div></a>`;
        });
        html += '</div></div>';
      }
      recentBar.innerHTML = html;
      recentBar.style.display = '';
      recentBar.querySelectorAll('.search-recent-chip').forEach(btn => {
        btn.addEventListener('click', () => {
          if (!searchInput) return;
          searchInput.value = btn.dataset.q;
          searchInput.dispatchEvent(new Event('input'));
          searchInput.focus();
        });
      });
      const rcCards = recentBar.querySelectorAll('.search-result-card');
      staggerCards(rcCards);
      bindTracking(rcCards);
    };

    // ── Scroll-lock helpers ────────────────────────────────────────────────
    /**
     * Lock body scroll while the overlay is open.
     * Captures the current scroll position, stops Lenis smooth scrolling,
     * and sets overflow hidden. (Requirements 14.2)
     */
    const lockScroll = () => {
      savedScrollY = window.scrollY;
      window.rflxnsLenis?.stop();
      document.body.style.overflow = 'hidden';
    };

    /**
     * Restore body scroll after the overlay closes.
     * Clears overflow hidden, restarts Lenis, and scrolls back to the
     * saved position so the page doesn't jump. (Requirements 14.3)
     */
    const restoreScroll = () => {
      document.body.style.overflow = '';
      window.rflxnsLenis?.start();
      window.scrollTo(0, savedScrollY);
    };

    /**
     * Return keyboard focus to the element that triggered the overlay open,
     * so AT users are not stranded after the overlay closes. (Requirements 13.9)
     */
    const returnFocusToTrigger = () => {
      if (lastActiveSearchTrigger) {
        lastActiveSearchTrigger.focus();
      } else {
        searchToggle?.focus();
      }
    };

    /**
     * Reset the search input and overlay back to the pre-typing baseline:
     * clear the input value, restore chips + featured, hide results/empty/error,
     * and reset keyboard selection. (Requirements 1.7)
     */
    const resetInput = () => {
      if (searchInput) searchInput.value = '';
      if (searchOverlay) searchOverlay.dataset.searchState = 'pre-typing';
      const defaultSuggestionsEl = document.getElementById('default-search-suggestions');
      const apiResultsEl         = document.getElementById('api-search-results');
      const searchChipsRowEl     = document.getElementById('search-chips-row');
      const searchEmptyStateEl   = document.getElementById('search-empty-state');
      const searchErrorEl        = document.getElementById('search-error-state');
      if (defaultSuggestionsEl) defaultSuggestionsEl.style.display = '';
      if (searchChipsRowEl)     searchChipsRowEl.style.display = '';
      if (apiResultsEl)         apiResultsEl.style.display = 'none';
      if (searchEmptyStateEl)   searchEmptyStateEl.style.display = 'none';
      if (searchErrorEl)        searchErrorEl.style.display = 'none';
      kbIndex = -1;
    };

    // ── Open / Close ───────────────────────────────────────────────────────
    const openSearchOverlay = () => {
      // Set active state and initial search state (Requirements 1.1, 1.2)
      searchOverlay.classList.add('active');
      searchOverlay.dataset.searchState = 'pre-typing';
      kbIndex = -1;

      if (homeMobileDockSearch) homeMobileDockSearch.classList.add('is-active');

      // GSAP entrance animation with reduced-motion guard (Requirements 1.2, 1.6)
      if (typeof gsap !== 'undefined' && !reduceMotion) {
        gsap.fromTo(
          searchOverlay,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
        );
      }
      // When reduceMotion is true, skip GSAP — the CSS `transition: opacity 0.08s`
      // handles the visibility change via the .active class alone. (Requirement 1.6)

      // Move focus to the input shortly after animation starts (Requirements 1.3, 13.1)
      window.setTimeout(() => {
        searchInput?.focus();
        startTyping();
        renderRecentBar();
        handleViewportChange();
      }, 50);

      lockScroll();
      handleViewportChange();

      // Stagger default cards each open
      staggerCards(document.querySelectorAll('#default-search-suggestions .search-result-card'));
    };

    const closeSearchOverlay = () => {
      kbIndex = -1;
      stopTyping();

      if (homeMobileDockSearch) homeMobileDockSearch.classList.remove('is-active');

      if (searchOverlay) {
        searchOverlay.style.height = '';
        searchOverlay.style.top = '';
      }

      // GSAP exit animation with reduced-motion guard (Requirements 1.4, 1.6, 1.8)
      if (typeof gsap !== 'undefined' && !reduceMotion) {
        gsap.to(searchOverlay, {
          opacity: 0,
          y: 20,
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            searchOverlay.classList.remove('active');
            if (searchOverlay) {
              searchOverlay.style.height = '';
              searchOverlay.style.top = '';
            }
            resetInput();
            restoreScroll();
            returnFocusToTrigger();
          },
        });
      } else {
        // Reduced-motion: immediately remove .active and clean up (Requirement 1.6)
        searchOverlay.classList.remove('active');
        if (searchOverlay) {
          searchOverlay.style.height = '';
          searchOverlay.style.top = '';
        }
        resetInput();
        restoreScroll();
        returnFocusToTrigger();
      }
    };

    const handleViewportChange = () => {
      if (searchOverlay && searchOverlay.classList.contains('active') && window.visualViewport) {
        const height = window.visualViewport.height;
        const offsetTop = window.visualViewport.offsetTop;
        searchOverlay.style.height = `${height}px`;
        searchOverlay.style.top = `${offsetTop}px`;
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }

    searchToggle?.addEventListener('click', (e) => {
      lastActiveSearchTrigger = e.currentTarget;
      openSearchOverlay();
    });
    homeMobileDockSearch?.addEventListener('click', (e) => {
      lastActiveSearchTrigger = e.currentTarget;
      openSearchOverlay();
    });
    document.getElementById('desktop-search-trigger')?.addEventListener('click', (e) => {
      lastActiveSearchTrigger = e.currentTarget;
      openSearchOverlay();
    });
    document.querySelectorAll('[data-search-trigger]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        lastActiveSearchTrigger = e.currentTarget;
        openSearchOverlay();
      });
    });
    searchClose?.addEventListener('click', closeSearchOverlay);

    // ── Bind default featured cards ────────────────────────────────────────
    bindTracking(document.querySelectorAll('#default-search-suggestions .search-result-card'));

    // ── Predictive Search ──────────────────────────────────────────────────
    const defaultSuggestions = document.getElementById('default-search-suggestions');
    const apiResults          = document.getElementById('api-search-results');
    const searchChipsRow      = document.getElementById('search-chips-row');
    let debounceTimer;

    /**
     * Show the empty-state panel and hide the results region.
     * (Requirements 6.9, 11.1, 11.7)
     */
    const showEmptyState = (origin) => {
      const emptyEl = document.getElementById('search-empty-state');
      if (emptyEl) emptyEl.style.display = '';
      if (apiResults) apiResults.style.display = 'none';
      setSearchState('empty', origin);
    };

    /**
     * Build client-side product cards from the predictive-search API response.
     * Uses formatProductName and formatINR from the pure helpers.
     * Adds the crimson CHAPTER I badge on every card.
     * Sets loading="lazy" and explicit width/height on images.
     * Structurally omits any scarcity badge (no inventory from suggest.json).
     * Preserves API order.
     * (Requirements 6.3, 6.7, 6.8, 7.2, 7.7, 9.1, 10.1, 12.6, 14.4)
     */
    const renderProducts = (products) => {
      if (!apiResults) return;
      if (products.length === 0) {
        showEmptyState();
        return;
      }
      let html = '<div class="search-results-grid">';
      products.forEach((product, i) => {
        // Parse naming convention from title
        const parts = (product.title || '').split(' / ').map(s => s.trim()).filter(Boolean);
        const nameFormatted = parts.length === 3
          ? formatProductName({ name: parts[0], number: parts[1], descriptor: parts[2] })
          : formatProductName({ title: product.title });
        const priceFormatted = formatINR(product.price);
        const img = product.featured_image
          ? `<img class="search-result-card__img" src="${product.featured_image.url}&width=300" alt="${product.title}" loading="lazy" width="150" height="200">`
          : `<div class="search-result-card__img"></div>`;
        html += `
      <a href="${product.url}" class="search-result-card" style="--i:${i}">
        <div class="search-result-card__media">
          <span class="chapter-badge" aria-label="Chapter I">CHAPTER I</span>
          ${img}
        </div>
        <div class="search-result-card__body">
          <span class="search-result-card__name">${nameFormatted}</span>
          <span class="search-result-card__price">${priceFormatted}</span>
        </div>
      </a>`;
      });
      html += '</div>';
      apiResults.innerHTML = html;
      apiResults.style.display = 'block';
      const newCards = apiResults.querySelectorAll('.search-result-card');
      staggerCards(newCards);
      bindTracking(newCards);
    };

    // ── State machine helper ───────────────────────────────────────────────
    /**
     * Set the overlay's data-search-state attribute and optionally a
     * data-empty-origin sub-flag (used when a shortcut-filtered view yields
     * zero results so CSS can keep the chips row visible — Requirement 4.8).
     * (Requirements 4.1, 4.5, 4.6, 4.8, 5.5, 5.6, 11.7)
     */
    const setSearchState = (state, emptyOrigin) => {
      searchOverlay.dataset.searchState = state;
      if (emptyOrigin) {
        searchOverlay.dataset.emptyOrigin = emptyOrigin;
      } else {
        delete searchOverlay.dataset.emptyOrigin;
      }
    };

    // ── Debounced fetch with AbortController stale guard ──────────────────
    // (Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.9, 6.10, 8.1, 8.2,
    //  8.3, 11.1, 11.6, 14.1, 14.5)
    let activeController = null;
    let requestSeq = 0;
    let currentRenderedQuery = '';

    const runQuery = (query) => {
      const mySeq = ++requestSeq;
      if (activeController) activeController.abort();
      activeController = new AbortController();
      const timeoutId = setTimeout(() => activeController.abort(), 10000);

      setSearchState('loading');
      if (apiResults) {
        apiResults.style.display = 'block';
        apiResults.innerHTML = '<div class="search-loading"><span class="search-results-title" style="display:block;text-align:center;">Searching...</span></div>';
      }
      const emptyEl = document.getElementById('search-empty-state');
      if (emptyEl) emptyEl.style.display = 'none';

      fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=5`,
        { signal: activeController.signal }
      )
        .then(r => r.json())
        .then(data => {
          clearTimeout(timeoutId);
          if (mySeq !== requestSeq) return; // stale response — discard
          const rawProducts = data?.resources?.results?.products || [];
          const products = excludeTestSkus(rawProducts, searchCfg);
          currentRenderedQuery = query;
          if (products.length === 0) {
            if (apiResults) apiResults.style.display = 'none';
            showEmptyState();
          } else {
            renderProducts(products);
            setSearchState('results');
          }
        })
        .catch(err => {
          clearTimeout(timeoutId);
          if (mySeq !== requestSeq) return;
          if (err.name === 'AbortError') {
            // Timeout or superseded — only show error if nothing is rendered for this query
            if (currentRenderedQuery !== query) {
              if (apiResults) {
                apiResults.style.display = 'block';
                apiResults.innerHTML = '<div class="search-error"><span class="search-results-title" style="display:block;text-align:center;color:rgba(245,240,235,0.5);">Could not reach Chapter I — try again.</span></div>';
              }
              setSearchState('error');
            }
          } else {
            if (apiResults) {
              apiResults.style.display = 'block';
              apiResults.innerHTML = '<div class="search-error"><span class="search-results-title" style="display:block;text-align:center;color:rgba(245,240,235,0.5);">Could not reach Chapter I — try again.</span></div>';
            }
            setSearchState('error');
          }
        });
    };

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        kbIndex = -1;
        document.querySelectorAll('.search-result-card').forEach(c => {
          c.classList.remove('is-kb-focused');
          c.setAttribute('aria-selected', 'false');
        });
        clearTimeout(debounceTimer);
        if (query.length === 0) {
          stopTyping(); startTyping();
          if (defaultSuggestions) defaultSuggestions.style.display = '';
          if (apiResults) apiResults.style.display = 'none';
          if (searchChipsRow) searchChipsRow.style.display = '';
          const emptyEl = document.getElementById('search-empty-state');
          if (emptyEl) emptyEl.style.display = 'none';
          renderRecentBar();
          setSearchState('pre-typing');
          return;
        }
        stopTyping();
        if (defaultSuggestions) defaultSuggestions.style.display = 'none';
        if (searchChipsRow) searchChipsRow.style.display = 'none';
        if (recentBar) recentBar.style.display = 'none';
        debounceTimer = setTimeout(() => runQuery(query), 260);
      });
      searchInput.closest('form')?.addEventListener('submit', () => saveSearch(searchInput.value.trim()));
    }

    // ── Chapter shortcut click handling ───────────────────────────────────────
    // Intercept pill clicks (progressive enhancement over the real link).
    // Fetch/filter the shortcut collection's products and render them in-overlay.
    // On zero eligible products, show the Empty_State while keeping chips visible.
    // (Requirements 4.3, 4.8, 8.1)
    document.querySelectorAll('#search-chips-row .search-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        const handle = chip.dataset.shortcutHandle;
        if (!handle) return;
        e.preventDefault();

        // Hide featured, show results area with loading
        if (defaultSuggestions) defaultSuggestions.style.display = 'none';
        if (recentBar) recentBar.style.display = 'none';
        if (apiResults) {
          apiResults.style.display = 'block';
          apiResults.innerHTML = '<div class="search-loading"><span class="search-results-title" style="display:block;text-align:center;">Loading...</span></div>';
        }
        const emptyEl = document.getElementById('search-empty-state');
        if (emptyEl) emptyEl.style.display = 'none';

        // For "all" handle, use all products URL; otherwise use collection URL
        const url = handle === 'all'
          ? `/search/suggest.json?q=*&resources[type]=product&resources[limit]=5`
          : `/search/suggest.json?q=*&resources[type]=product&resources[limit]=5&resources[options][prefix]=last`;

        // Fetch products for this shortcut
        const mySeq = ++requestSeq;
        if (activeController) activeController.abort();
        activeController = new AbortController();

        fetch(
          `/search/suggest.json?q=&resources[type]=product&resources[limit]=5`,
          { signal: activeController.signal }
        )
          .then(r => r.json())
          .then(data => {
            if (mySeq !== requestSeq) return;
            const rawProducts = data?.resources?.results?.products || [];
            const products = excludeTestSkus(rawProducts, searchCfg);
            if (products.length === 0) {
              // Show empty state but keep chips row visible (Requirement 4.8)
              if (apiResults) apiResults.style.display = 'none';
              if (emptyEl) emptyEl.style.display = '';
              setSearchState('empty', 'shortcut');
            } else {
              renderProducts(products);
              setSearchState('results');
            }
          })
          .catch(() => {
            if (mySeq !== requestSeq) return;
            if (apiResults) {
              apiResults.style.display = 'block';
              apiResults.innerHTML = '<div class="search-error"><span class="search-results-title" style="display:block;text-align:center;color:rgba(245,240,235,0.5);">Could not load pieces — try again.</span></div>';
            }
          });
      });
    });
  }

  let accountNavigationInFlight = false;

  /**
   * Reset the account transition overlay after browser history restores the
   * page, such as when the user navigates back from account pages.
   */
  const resetAccountTransition = () => {
    document.body.classList.remove('account-transition-is-active');
    if (accountTransitionOverlay) {
      accountTransitionOverlay.classList.remove('is-active');
      accountTransitionOverlay.setAttribute('aria-hidden', 'true');
    }
    accountTransitionLinks.forEach((link) => link.classList.remove('is-loading'));
    accountNavigationInFlight = false;
  };

  /**
   * Skip the custom account transition when the browser should handle the click
   * natively, such as modifier-clicks, downloads, or new-tab navigation.
   */
  const shouldBypassTransition = (event, link) =>
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    link.target === '_blank' ||
    link.hasAttribute('download');

  /**
   * Start the branded account transition overlay before leaving the current
   * page for the destination account route.
   */
  const beginAccountTransition = (link) => {
    if (accountNavigationInFlight) return;

    const destination = link.getAttribute('href');
    if (!destination) return;

    accountNavigationInFlight = true;
    link.classList.add('is-loading');

    if (mobileMenu?.classList.contains('is-open')) {
      mobileMenu.classList.remove('is-open');
    }

    document.body.style.overflow = '';
    document.body.classList.add('account-transition-is-active');

    if (accountTransitionOverlay) {
      accountTransitionOverlay.classList.add('is-active');
      accountTransitionOverlay.setAttribute('aria-hidden', 'false');

      if (typeof gsap !== 'undefined' && !reduceMotion) {
        const shell = accountTransitionOverlay.querySelector('.account-transition-shell');
        if (shell) {
          gsap.killTweensOf(shell);
          gsap.fromTo(
            shell,
            { opacity: 0, y: 14, scale: 0.985 },
            { opacity: 1, y: 0, scale: 1, duration: 0.28, ease: 'power2.out' }
          );
        }
      }
    }

    window.setTimeout(() => {
      window.location.assign(destination);
    }, reduceMotion ? 40 : 160);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-account-transition]');
    if (!link) return;
    if (shouldBypassTransition(event, link)) return;
    event.preventDefault();
    beginAccountTransition(link);
  });

  window.addEventListener('pageshow', resetAccountTransition);

  if (mobileMenuTrigger && mobileMenu) {
    const check = document.getElementById('check');

    /**
     * Close the mobile navigation and release body scroll locking regardless of
     * which close affordance was used.
     */
    const closeMobileMenu = () => {
      mobileMenu.classList.remove('is-open');
      document.body.style.overflow = '';
      if (check) check.checked = false;
    };

    mobileMenuTrigger.addEventListener('click', () => {
      if (check) {
        window.setTimeout(() => {
          const isOpen = Boolean(check.checked);
          mobileMenu.classList.toggle('is-open', isOpen);
          document.body.style.overflow = isOpen ? 'hidden' : '';
        }, 10);
        return;
      }

      mobileMenu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    mobileMenuClose?.addEventListener('click', closeMobileMenu);
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    mobileMenuOverlay?.addEventListener('click', closeMobileMenu);
    mobileMenu.addEventListener('click', (e) => {
      if (e.target === mobileMenu || e.target === mobileMenuOverlay) {
        closeMobileMenu();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMobileMenu();
      }
    });
    mobileMenu.querySelector('.mobile-menu-close-bar')?.addEventListener('click', closeMobileMenu);
    mobileMenu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMobileMenu);
    });
  }

  const handleAnchorNavigation = (targetSelector, event) => {
    if (!targetSelector || targetSelector === '#story' || targetSelector === '#story-overlay') return;
    const target = document.querySelector(targetSelector);
    if (!target) return;

    if (event) event.preventDefault();
    if (window.rflxnsLenis && typeof window.rflxnsLenis.scrollTo === 'function') {
      window.rflxnsLenis.scrollTo(target, { offset: -60, duration: 1.05 });
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  document.querySelectorAll('a[href^="#"], a[href^="/#"]').forEach((anchor) => {
    anchor.addEventListener('click', (event) => {
      const href = anchor.getAttribute('href');
      if (!href) return;
      
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/').replace(/\/$/, '');
      const normPath = window.location.pathname.replace(/\/$/, '');
      const isHomepage = normPath === rootPath || normPath === '' || normPath.endsWith('/index');
      if (href.startsWith('/#')) {
        if (isHomepage) {
          const targetId = href.replace('/#', '#');
          handleAnchorNavigation(targetId, event);
        }
      } else if (href.startsWith('#')) {
        handleAnchorNavigation(href, event);
      }
    });
  });

  // Handle direct hash navigation on page load
  if (window.location.hash) {
    window.setTimeout(() => {
      handleAnchorNavigation(window.location.hash, null);
    }, 400);
  }

  // --------------------------------------------------------------------------
  // Audio Synthesis & Haptic Clicks
  // --------------------------------------------------------------------------
  let audioCtx = null;
  const getAudioContext = () => {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    return audioCtx;
  };

  window.playClickSound = function(pitchFactor = 1.0) {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // Node 1: High transient "tick"
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(4500 * pitchFactor, now);
      osc1.frequency.exponentialRampToValueAtTime(1000 * pitchFactor, now + 0.006);
      gain1.gain.setValueAtTime(0.03, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.006);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);

      // Node 2: Mid mechanical "tock"
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(1200 * pitchFactor, now);
      osc2.frequency.exponentialRampToValueAtTime(300 * pitchFactor, now + 0.018);
      gain2.gain.setValueAtTime(0.02, now);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.018);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);

      osc1.start(now);
      osc1.stop(now + 0.01);
      osc2.start(now);
      osc2.stop(now + 0.02);
    } catch (e) {
      // Fail silently
    }
  };

  window.playSuccessChime = function() {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      const playNote = (freq, startTime, duration, volume) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(volume, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };

      // Futuristic golden ratio chime: E5 -> B5
      playNote(659.25, now, 0.12, 0.025);
      playNote(987.77, now + 0.06, 0.18, 0.018);
    } catch (e) {
      // Fail silently
    }
  };

  // Bind clicks globally to play click sounds
  document.addEventListener('click', (event) => {
    const target = event.target;
    const interactive = target.closest('a, button, input[type="submit"], input[type="button"], input[type="radio"], input[type="checkbox"], label, [role="button"], .search-chip, [data-pdp-option-button], .home-mobile-dock__item');
    if (interactive) {
      if (interactive.tagName === 'INPUT' && !['submit', 'button', 'radio', 'checkbox'].includes(interactive.type)) {
        return;
      }
      
      const isCta = interactive.closest('[data-pdp-add-to-bag], .product-form__submit, #PasswordSwipeSubmit, [data-pdp-buy-now]');
      if (isCta) {
        window.playClickSound(0.75); // Deeper pitch
      } else {
        window.playClickSound(1.0); // Standard pitch
      }
    }
  });

  // Bind keydown globally to play high typewriter ticks for inputs
  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (target.closest('.minimal-input, #search-input, #Password, .rflxns-cart-discount__input')) {
      if (event.key.length === 1 || event.key === 'Backspace') {
        window.playClickSound(1.4); // Very high-pitched transient tick
      }
    }
  });

  // --------------------------------------------------------------------------
  // GPU-Accelerated Custom Cursor Glow
  // --------------------------------------------------------------------------
  if (!('ontouchstart' in window)) {
    const cursor = document.createElement('div');
    cursor.className = 'custom-cursor';
    
    const dot = document.createElement('div');
    dot.className = 'custom-cursor-dot';
    cursor.appendChild(dot);
    
    document.body.appendChild(cursor);

    let mouseX = -1000;
    let mouseY = -1000;
    let currentX = -1000;
    let currentY = -1000;
    let isVisible = false;

    document.addEventListener('mousemove', (event) => {
      mouseX = event.clientX;
      mouseY = event.clientY;
      if (!isVisible) {
        isVisible = true;
        cursor.style.opacity = '1';
        currentX = mouseX;
        currentY = mouseY;
      }
    });

    const tick = () => {
      const ease = 0.15;
      currentX += (mouseX - currentX) * ease;
      currentY += (mouseY - currentY) * ease;

      cursor.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) translate(-50%, -50%)`;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    document.addEventListener('mouseleave', () => {
      cursor.style.opacity = '0';
      isVisible = false;
    });

    document.addEventListener('mouseenter', () => {
      cursor.style.opacity = '1';
      isVisible = true;
    });

    document.addEventListener('mouseover', (event) => {
      const target = event.target;
      if (target.closest('a, button, input, select, textarea, label, [role="button"], .search-chip, [data-pdp-option-button], [data-account-transition], .home-mobile-dock__item')) {
        cursor.classList.add('is-hovered');
      }
    });

    document.addEventListener('mouseout', (event) => {
      const target = event.target;
      if (target.closest('a, button, input, select, textarea, label, [role="button"], .search-chip, [data-pdp-option-button], [data-account-transition], .home-mobile-dock__item')) {
        cursor.classList.remove('is-hovered');
      }
    });
  }

  // Draggable Sliding Pill Logic
  const mobileDock = document.getElementById('home-mobile-dock');
  const dockIndicator = document.getElementById('dock-indicator');
  if (mobileDock && dockIndicator) {
    const items = Array.from(mobileDock.querySelectorAll('.home-mobile-dock__item')).filter(
      item => item.offsetWidth > 0 || item.getBoundingClientRect().width > 0
    );
    let isDragging = false;
    let startX = 0;
    let initialIndicatorX = 0;
    let dragDistance = 0;

    const updateIndicator = (activeItem, animate = false) => {
      if (!activeItem) return;
      const itemLeft = activeItem.offsetLeft;
      const itemTop = activeItem.offsetTop;
      const itemWidth = activeItem.offsetWidth;
      const itemHeight = activeItem.offsetHeight;

      if (animate) {
        dockIndicator.style.transition = 'transform 150ms ease-out, width 150ms ease-out, height 150ms ease-out';
      } else {
        dockIndicator.style.transition = 'none';
      }
      dockIndicator.style.transform = `translate(${itemLeft}px, ${itemTop}px)`;
      dockIndicator.style.width = `${itemWidth}px`;
      dockIndicator.style.height = `${itemHeight}px`;
      
      items.forEach(i => i.classList.remove('home-mobile-dock__item--active', 'is-active'));
      activeItem.classList.add('home-mobile-dock__item--active');
    };

    let activeItem = mobileDock.querySelector('.home-mobile-dock__item--active') || items[0];
    
    requestAnimationFrame(() => {
      updateIndicator(activeItem, false);
    });

    window.addEventListener('resize', () => {
      if (!isDragging) {
        activeItem = mobileDock.querySelector('.home-mobile-dock__item--active') || items[0];
        updateIndicator(activeItem, false);
      }
    });

    const onDragStart = (e) => {
      if (window.innerWidth > 767) return; 
      isDragging = true;
      startX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      
      const transform = window.getComputedStyle(dockIndicator).getPropertyValue('transform');
      let matrix = new WebKitCSSMatrix(transform === 'none' ? 'matrix(1, 0, 0, 1, 0, 0)' : transform);
      initialIndicatorX = matrix.m41;
      dragDistance = 0;

      dockIndicator.style.transition = 'none';
    };

    const onDragMove = (e) => {
      if (!isDragging || window.innerWidth > 767) return;
      e.preventDefault(); 
      const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      const deltaX = clientX - startX;
      dragDistance = Math.abs(deltaX);

      const pillWidth = mobileDock.getBoundingClientRect().width;
      const indicatorWidth = dockIndicator.getBoundingClientRect().width;
      
      let dragX = initialIndicatorX + deltaX;
      dragX = Math.max(0, Math.min(dragX, pillWidth - indicatorWidth));
      
      const transform = window.getComputedStyle(dockIndicator).getPropertyValue('transform');
      let matrix = new WebKitCSSMatrix(transform === 'none' ? 'matrix(1, 0, 0, 1, 0, 0)' : transform);
      const currentY = matrix.m42;

      dockIndicator.style.transform = `translate(${dragX}px, ${currentY}px)`;
    };

    const onDragEnd = (e) => {
      if (!isDragging || window.innerWidth > 767) return;
      isDragging = false;
      
      const transform = window.getComputedStyle(dockIndicator).getPropertyValue('transform');
      let matrix = new WebKitCSSMatrix(transform === 'none' ? 'matrix(1, 0, 0, 1, 0, 0)' : transform);
      const finalX = matrix.m41;
      const indicatorCenter = finalX + (dockIndicator.offsetWidth / 2);

      let nearestItem = items[0];
      let minDistance = Infinity;

      items.forEach(item => {
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const distance = Math.abs(itemCenter - indicatorCenter);
        if (distance < minDistance) {
          minDistance = distance;
          nearestItem = item;
        }
      });

      updateIndicator(nearestItem, true);

      if (dragDistance > 8) {
        const href = nearestItem.getAttribute('href');
        if (href.startsWith('#')) {
          const targetEl = document.getElementById(href.substring(1));
          if (targetEl) targetEl.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.href = href;
        }
      }
    };

    mobileDock.addEventListener('mousedown', onDragStart);
    window.addEventListener('mousemove', onDragMove, { passive: false });
    window.addEventListener('mouseup', onDragEnd);

    mobileDock.addEventListener('touchstart', onDragStart, { passive: false });
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);

    items.forEach(item => {
      item.addEventListener('click', (e) => {
        if (dragDistance > 8 && window.innerWidth <= 767) {
          e.preventDefault(); 
        } else {
          activeItem = item;
          updateIndicator(item, true);
        }
      });
    });

    // Sync indicator with active class changes from scroll observer
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('home-mobile-dock__item--active')) {
              if (target !== activeItem) {
                activeItem = target;
                updateIndicator(activeItem, true);
              }
            }
          }
        });
      });
      items.forEach(item => observer.observe(item, { attributes: true }));
    }
  }
});
